import "./SingleAccountOrderDialogComponent.scss";
import template from "./SingleAccountOrderDialogComponent.html";
import {IComponentOptions, IFormController, IOnDestroy, IOnInit, ITimeoutService} from "angular";
import {IModalInstanceService} from "angular-ui-bootstrap";
import {BehaviorSubject, Observable, ReplaySubject} from "rxjs";
import {isNotNull} from "../../../../../js/util/UtilFunctions";
import {OrderLifetime, OrderSide} from "../../../../../orders/models/OrderRecord";
import {PricingInstruction} from "../../../../../orders/models/PricingInstruction";
import {QuoteViewModel} from "../../../../../quote/models/QuoteViewModel";
import {PortfolioService} from "../../../../service/PortfolioService";
import {BrokerageEstimationService} from "../../../../../brokerage/services/BrokerageEstimationService";
import {ObjectUtil} from "../../../../../js/util/ObjectUtil";
import {EquityPosition, PortfolioPositions, PortfolioType} from "../../../../models/Portfolio";
import {FeatureNames} from "../../../../../types/UserConfig";
import {CurrentUserService} from "../../../../../services/user/CurrentUserService";
import {PriceStrategy} from "../../../../../components/inputs/price/input/PriceStrategy";
import {PriceStrategyService} from "../../../../../components/inputs/price/service/PriceStrategyService";
import {CompanyInfo} from "../../../../../types/Company";
import {NgFormUtil} from "../../../../../js/util/NgFormUtil";
import {AccountBriefRecord} from "../../../../../account/types/AccountBriefRecord";
import {MultiOrderStore} from "../../place-multi-equity-order-wizard/store/MultiOrderStore";
import {EquityOrderWizardStore} from "../../place-equity-order-wizard/store/EquityOrderWizardStore";
import {EquityOrderType, EquityWizardData} from "../../place-equity-order-wizard/models/EquityWizardData";
import {AccountSource} from "../../../../../account/types/AccountSource";
import {QuoteStore} from "../../../../../quote/store/QuoteStore";
import {AccountStore} from "../../../../../account/store/AccountStore";
import {ReadonlyAccountStore} from "../../../../../account/store/ReadonlyAccountStore";
import {extractOrderRequest, isSameBrokerageRequest} from "../../place-equity-order-wizard/util/EquityWizardOrderUtil";
import {PlaceOrderRequest} from "../../services/models/PlaceOrderRequest";
import {ComponentCallback} from "../../../../../types/AngularJS";
import {AsxInstrumentUtil} from "../../../../../js/util/asx/AsxInstrumentUtil";
import {Exchange} from "../../../../../instruments/exchange/Exchange";

export const SingleAccountOrderDialogComponent = "tfSingleAccountOrderDialog";

export function singleAccountOrdersDialog(): IComponentOptions {
    return {
        template,
        bindings: {
            orderStore: "<",
            modalInstance: "<",
            onAccountBalanceChange: "&",
            resolve: "<",
        },
        controllerAs: "ctrl",
        controller: SingleAccountOrdersDialogComponentController
    };
}

interface Resolve {
    orderStore: MultiOrderStore;
    readonly singleOrder: EquityWizardData;
    index: number | null;
}

export class SingleAccountOrdersDialogComponentController implements IOnInit, IOnDestroy {

    /** @ui-binding */
    public set orderForm(form: IFormController | null) {
        if (form != null) {
            this.orderForm$.next(form);
            this.form = form;
        }
    }
    /* Need access to the form in both the template and the observables, so we reassign to a controller variable */
    public form: IFormController;
    private readonly orderForm$ = new ReplaySubject<IFormController>(1);

    public store: EquityOrderWizardStore;

    /** @input */
    public index: number | null;
    public readonly isEdit$ = new ReplaySubject<boolean>(1);
    public orderStore: MultiOrderStore;
    public defaultOrder: EquityWizardData;
    public readonly modalInstance: IModalInstanceService;

    /** @output */
    public readonly onAccountBalanceChange: ComponentCallback;

    public accountBalance: number;
    public readonly stopLossEnabled$ = new ReplaySubject<boolean>(1);
    public readonly code$ = new ReplaySubject<string>(1);
    public readonly exchange$ = new ReplaySubject<string>(1);
    public readonly pricingInstruction$ = new BehaviorSubject<PricingInstruction>(PricingInstruction.LIMIT);
    public readonly hasAccountSelected$ = new BehaviorSubject(false);
    public readonly quantityOwned$ = new BehaviorSubject(0);
    public readonly estimatedBrokerage$ = new BehaviorSubject<number | null>(null);
    public readonly quote$ = new ReplaySubject<QuoteViewModel>(1);

    /* Stupid NgModel bindings */
    public portfolio?: AccountBriefRecord | null;
    public side: OrderSide;
    public lifetime: OrderLifetime;
    public isParentOrder: boolean | null = false;
    public quantity: number | null;
    public totalValue: number;
    public consideration: number;
    public orderType: EquityOrderType;
    public strategy: PriceStrategy;
    public isStopLoss = false;
    public stopLossLimit: number | null = null;
    public isOma: boolean = false;
    public accountHasOrdered: boolean = false;
    private readonly destroyed$ = new ReplaySubject<boolean>(1);

    // Based of parent wizard
    public currentAccount$ = new ReplaySubject<PortfolioPositions>(1);
    public currentAccountBrief$: Observable<AccountBriefRecord>;
    public accountSource: AccountSource;
    private quoteStore: QuoteStore;

    /** @input */
    public set resolve(resolve: Resolve) {
        this.index = resolve.index;
        this.isEdit$.next(resolve.singleOrder != null && resolve.index != null);

        if (resolve.orderStore != null) {
            this.orderStore = resolve.orderStore;
        }
        if (resolve.singleOrder != null && resolve.index != null) {
            this.portfolio = resolve.singleOrder.portfolio;
            this.defaultOrder = resolve.singleOrder;

        } else if (resolve.singleOrder !== null && resolve.index == null) {
            this.defaultOrder = resolve.singleOrder;
        }

    }

    /** @ngInject */
    public constructor(private readonly priceStrategyService: PriceStrategyService,
                       private readonly portfolioService: PortfolioService,
                       private readonly brokerageEstimationService: BrokerageEstimationService,
                       private readonly $timeout: ITimeoutService,
                       private readonly currentUserService: CurrentUserService,
                       quoteStore: QuoteStore,
                       accountStore: AccountStore) {
        this.quoteStore = quoteStore;
        this.accountSource = ReadonlyAccountStore.create(accountStore);
    }

    public $onInit(): void {
        console.log("onInit triggered", this.defaultOrder);
        this.store = new EquityOrderWizardStore(this.quoteStore, this.accountSource, this.initOrder(this.defaultOrder));
        this.currentAccountBrief$ = this.buildCurrentAccountBrief();
        this.currentUserService.current.subscribe(currentUser => this.isOma = currentUser.isOma());
        this.watchStopLossEnabled();
        this.watchCode();
        this.watchExchange();
        this.watchSelectedAccount();
        this.watchQuantityOwnedAndAccountBalance();
        this.watchQuoteChange();
        this.watchBrokerageEstimate();
        this.watchCodeChange();
        this.watchOrderProperties();
        this.watchOrderChanges();
        this.watchPricingInstruction();
        this.watchAccountSelection();
        this.watchIsAccountExist();
        this.accountSource
            .current
            .takeUntil(this.destroyed$)
            .filter(isNotNull)
            .map((account) => account.id)
            .switchMap((id) => this.fetchPositions(id))
            .subscribe((positions) => this.currentAccount$.next(positions));
    }

    private initOrder(orderPrototype: Partial<EquityWizardData> | null): EquityWizardData {
        const code = orderPrototype?.code;
        let exchange = orderPrototype?.exchange;
        if ((exchange == null) && (code != null)) {
            exchange = AsxInstrumentUtil.guessExchange(code);
        }

        let strategy: PriceStrategy | undefined;
        if (code != null) {
            strategy = this.priceStrategyService.forCode(code);
        }

        let lifetime: OrderLifetime | undefined = orderPrototype?.lifetime ?? OrderLifetime.GOOD_TILL_CANCEL;
        if (exchange === Exchange.AXW || exchange === Exchange.CXA) {
            lifetime = OrderLifetime.END_OF_DAY;
        }
        const name = orderPrototype?.name;
        const orderNumber = orderPrototype?.orderNumber;
        const quantity = orderPrototype?.quantity ?? 0;
        const side = orderPrototype?.side ?? OrderSide.BUY;
        const type = EquityOrderType.QUANTITY;
        const limit = orderPrototype?.limit;
        const portfolio = orderPrototype?.portfolio;
        const quote = orderPrototype?.quote;
        const pricingInstruction = orderPrototype?.pricingInstruction ?? PricingInstruction.LIMIT;
        const totalValue = orderPrototype?.totalValue ?? 0;
        const isStopLoss = orderPrototype?.isStopLoss ?? false;
        const isParentOrder = orderPrototype?.isParentOrder ?? false;
        const childOrders = orderPrototype?.childOrders ?? [];

        return {
            orderNumber,
            code,
            name,
            exchange,
            side,
            quantity: Math.abs(quantity),
            limit,
            lifetime,
            pricingInstruction,
            type,
            totalValue,
            portfolio,
            quote,
            strategy,
            isStopLoss,
            isParentOrder,
            childOrders
        };
    }

    private buildCurrentAccountBrief(): Observable<AccountBriefRecord> {
        return this.accountSource
                   .current
                   .takeUntil(this.destroyed$)
                   .filter(isNotNull)
                   .publishReplay(1)
                   .refCount();
    }

    private watchAccountSelection() {
        this.accountSource
            .current
            .takeUntil(this.destroyed$)
            .filter(isNotNull)
            .subscribe((portfolio) => this.store.updatePortfolio(portfolio));
    }

    private watchIsAccountExist() {
        this.accountSource
            .current
            .takeUntil(this.destroyed$)
            .filter(isNotNull)
            .subscribe((portfolio) => this.store.updatePortfolio(portfolio));
        Observable.combineLatest(this.accountSource.current, this.orderStore.orders$)
            .takeUntil(this.destroyed$)
            .subscribe(
                ([current, orders]) => {
                  const accounts = orders.map(o => o.portfolio?.number);
                  const currentAccount = current?.number;
                  // this.accountAlreadyAdded$.next(accounts.includes(currentAccount));
                  this.accountHasOrdered = accounts.includes(currentAccount);
                }
            );
    }

    public companyChanged(company: CompanyInfo) {
        this.orderForm$
            .first()
            .subscribe((form) => NgFormUtil.setPristine(form));

        const security = ObjectUtil.pick(company, "code", "exchange");
        const strategy = this.priceStrategyService.forCode(security.code);
        if (security.exchange === Exchange.CXA || security.exchange === Exchange.AXW) {
            this.store.updateLifetime(OrderLifetime.END_OF_DAY);
        }
        this.store.updateSecurity(security, strategy);
        this.store.updateName(company.name);
    }

    private watchStopLossEnabled() {
        const hasFeature$ = this.currentUserService
                                .current
                                .map((user) => user.hasFeature(FeatureNames.STOP_LOSS_ORDERS));

        const isValidAccount$ = this.store
                                    .current
                                    .map((data) => data.portfolio?.portfolioType)
                                    .map((type) => type === PortfolioType.BROKER || type === PortfolioType.SIMULATED);

        const isSell$ = this.store
                            .current
                            .map((order) => order.side)
                            .map((side) => side === OrderSide.LONG_SELL || side === OrderSide.SHORT_SELL);

        Observable.combineLatest(isSell$, hasFeature$, isValidAccount$)
                  .takeUntil(this.destroyed$)
                  .map(([isSell, featureEnabled, isValidAccount]) => isSell && featureEnabled && isValidAccount)
                  .distinctUntilChanged()
                  .subscribe((enabled) => this.stopLossEnabled$.next(enabled));
    }

    private watchPricingInstruction() {
        this.store
            .current
            .takeUntil(this.destroyed$)
            .map((d) => d.pricingInstruction)
            .filter(isNotNull)
            .distinctUntilChanged()
            .subscribe((pricingInstruction) => this.pricingInstruction$.next(pricingInstruction));
    }

    private watchCode() {
        this.store
            .current
            .takeUntil(this.destroyed$)
            .map((d) => d.code)
            .filter(isNotNull)
            .distinctUntilChanged()
            .subscribe((code) => this.code$.next(code));
    }

    private watchExchange() {
        this.store
            .current
            .takeUntil(this.destroyed$)
            .map((d) => d.exchange)
            .filter(isNotNull)
            .distinctUntilChanged()
            .subscribe((exchange) => this.exchange$.next(exchange));
    }

    private watchSelectedAccount() {
        this.store
            .current
            .takeUntil(this.destroyed$)
            .map((c) => c.portfolio)
            .map(isNotNull)
            .subscribe((hasAccount) => this.hasAccountSelected$.next(hasAccount));
    }

    private watchQuantityOwnedAndAccountBalance() {
        const portfolioPositions$ = this.buildCurrentPortfolio();
        const equityPositions$ = portfolioPositions$.map(p => p.equityPositions);
        const code$ = this.store.current.map((d) => d.code).filter(isNotNull);
        const source$ = Observable.combineLatest(code$, equityPositions$);

        portfolioPositions$.takeUntil(this.destroyed$)
                           .subscribe((position) => {
                               console.log("subscribe to position", position);
                               this.accountBalanceChanged(position.currentCmaBalance);
                           });

        source$.takeUntil(this.destroyed$)
               .map(([code, positions]) => this.findPositionQuantity(code, positions))
               .subscribe((quantity) => this.quantityOwned$.next(quantity));
    }

    private buildCurrentPortfolio(): Observable<PortfolioPositions> {
        return this.store
                   .current
                   .takeUntil(this.destroyed$)
                   .map((d) => d.portfolio)
                   .filter(isNotNull)
                   .map((account) => account.id)
                   .distinctUntilChanged()
                   .switchMap((id) => this.fetchPositions(id))
                   .map((p) => {
                       return p;
                   })
                   .publishReplay(1)
                   .refCount();
    }

    private fetchPositions(portfolioId: number): Observable<PortfolioPositions> {
        return Observable.of(portfolioId)
                         .flatMap((id) => this.portfolioService.portfolio(id))
                         .catch(() => Observable.empty<PortfolioPositions>());
    }

    private findPositionQuantity(code: string, positions: ReadonlyArray<EquityPosition>): number {
        const position = positions.find((p) => p.code === code);
        if (position == null) {
            return 0;
        } else {
            return position.quantity;
        }
    }

    private watchQuoteChange() {
        this.store
            .current
            .takeUntil(this.destroyed$)
            .map((d) => d.quote)
            .filter(isNotNull)
            .distinctUntilChanged()
            .subscribe((quote) => this.quote$.next(quote));
    }

    private watchBrokerageEstimate() {
        this.store
            .current
            .takeUntil(this.destroyed$)
            .filter((order) => order.quantity !== 0)
            .map((order) => extractOrderRequest(order))
            .filter(isNotNull)
            .filter((request) => request.order.quantity != null && request.order.quantity !== 0)
            .distinctUntilChanged(isSameBrokerageRequest())
            .auditTime(1000)
            .switchMap((request) => this.retrieveBrokerageQuote(request.portfolioId, request.order))
            .subscribe((estimate) => this.estimatedBrokerage$.next(estimate));
    }

    private retrieveBrokerageQuote(portfolioId: number, request: PlaceOrderRequest): Observable<number | null> {
        const estimationRequest = ObjectUtil.pick(request, "code", "buy", "quantity", "limit", "legs", "pricingInstruction", "exchange");
        const response = this.brokerageEstimationService.estimate(portfolioId, estimationRequest);
        return Observable.fromPromise(response)
                         .catch(() => Observable.of(null));
    }

    private watchCodeChange() {
        this.store
            .current
            .takeUntil(this.destroyed$)
            .map((d) => d.code)
            .distinctUntilChanged()
            .filter(isNotNull)
            .map((code) => this.priceStrategyService.forCode(code))
            .subscribe((strategy) => this.$timeout(() => this.strategy = strategy));
    }

    private watchOrderProperties() {
        this.store
            .current
            .takeUntil(this.destroyed$)
            .subscribe((order) => this.$timeout(() => {
                this.side = order.side;
                this.lifetime = order.lifetime;
                this.isParentOrder = order.isParentOrder;
                this.quantity = order.quantity;
                this.totalValue = order.totalValue;
                this.consideration = order.quantity * (order.limit ?? 0);
                this.orderType = order.type ?? EquityOrderType.QUANTITY;
                this.isStopLoss = order.isStopLoss;
                this.stopLossLimit = order.stopLossLimit ?? null;
            }));
    }

    private watchOrderChanges() {
        const source$ = Observable.combineLatest(this.store.current,
                                                 this.orderForm$,
                                                 this.quantityOwned$,
                                                 this.hasAccountSelected$);
        source$.takeUntil(this.destroyed$)
               .subscribe(([order, form, quantityOwned, hasAccount]) => this.updateFormValidity(order,
                                                                                                form,
                                                                                                quantityOwned,
                                                                                                hasAccount));
    }

    public setLimitToMid() {
        this.store.updateLimitToMid();
    }

    public limitChanged(limit: number | null) {
        if (limit != null) {
            this.store.updateLimit(limit);
        }
    }

    public pricingInstructionChanged(pricingInstruction: PricingInstruction) {
        if (PricingInstruction != null) {
            this.pricingInstruction$.next(pricingInstruction);
            this.store.updatePricingInstruction(pricingInstruction);
        }
    }

    public typeChanged(type: EquityOrderType) {
        this.store.updateOrderType(type);
    }

    public unitsChanged(units: number) {
        this.store.updateQuantity(units);
    }

    public totalValueChanged(totalValue: number) {
        this.store.updateTotalValue(totalValue);
    }

    public sideChanged(side: OrderSide) {
        this.store.updateSide(side);
    }

    public lifetimeChanged(lifetime: OrderLifetime) {
        this.store.updateLifetime(lifetime);
    }

    public isParentOrderChanged(isParentOrder: boolean) {
        this.store.updateIsParentOrder(isParentOrder);
        if (isParentOrder) {
            this.store.updateOrderType(EquityOrderType.QUANTITY);
        }
    }

    public toggleStopLoss() {
        this.store.toggleStopLoss();
    }

    public stopLossLimitChanged(limit: number) {
        this.store.changeStopLossLimit(limit);
    }

    private updateFormValidity(order: EquityWizardData,
                               form: IFormController,
                               quantityOwned: number,
                               hasAccount: boolean) {
        if (form["quantity"] == null) {
            return;
        }
        form.$setValidity("has-account-selected", hasAccount, form);
    }

    public accountBalanceChanged(accountBalance: number) {
        this.accountBalance = accountBalance;
    }

    public addSingleAccountOrder() {
        this.store
            .current
            .first()
            .subscribe( o => {
                this.orderStore.addOrder(
                    {
                        portfolio: o.portfolio,
                        orderNumber: o.orderNumber,
                        code: o.code,
                        exchange: o.exchange,
                        name: o.name,
                        side: o.side,
                        quantity: o.quantity,
                        limit: o.limit,
                        lifetime: o.lifetime,
                        expiry: o.expiry,
                        pricingInstruction: o.pricingInstruction,
                        type: o.type,
                        totalValue: o.totalValue,
                        advice: o.advice,
                        quote: o.quote,
                        strategy: o.strategy,
                        isStopLoss: o.isStopLoss,
                        stopLossLimit: o.stopLossLimit,
                        isParentOrder: o.isParentOrder,
                        childOrders: o.childOrders
                    }
                );
            });
        this.modalInstance.close();
    }

    public updateSingleAccountOrder() {
        this.store
            .current
            .first()
            .subscribe( o => {
                if (this.index != null) {
                    this.orderStore.updateOrder(
                        this.index,
                        {
                            portfolio: o.portfolio,
                            orderNumber: o.orderNumber,
                            code: o.code,
                            exchange: o.exchange,
                            name: o.name,
                            side: o.side,
                            quantity: o.quantity,
                            limit: o.limit,
                            lifetime: o.lifetime,
                            expiry: o.expiry,
                            pricingInstruction: o.pricingInstruction,
                            type: o.type,
                            totalValue: o.totalValue,
                            advice: o.advice,
                            quote: o.quote,
                            strategy: o.strategy,
                            isStopLoss: o.isStopLoss,
                            stopLossLimit: o.stopLossLimit,
                            isParentOrder: o.isParentOrder,
                            childOrders: o.childOrders
                        }
                    );
                    this.modalInstance.close();
                }
            });
    }

    public closeModal() {
        this.quantity = 0;
        this.modalInstance.dismiss();
    }

    public cancel() {
        this.modalInstance.dismiss();
    }

    public $onDestroy(): void {
        this.destroyed$.next(true);
        this.destroyed$.complete();
    }
}
